/**
 * Message Queue Utility
 * Handles offline message queuing with retry mechanism using localStorage
 * 
 * Optimization: Uses queueMicrotask to batch localStorage writes, reducing I/O overhead
 * when multiple operations occur in quick succession (e.g., add → updateStatus → update → remove)
 */

const MAX_QUEUE_SIZE = 50;

class MessageQueue {
    constructor() {
        this.queue = this.loadQueue();
        this.listeners = [];
        this._saveScheduled = false; // Track if a microtask save is pending
    }

    /**
     * Load queue from localStorage
     */
    loadQueue() {
        try {
            const saved = localStorage.getItem('chat_message_queue');
            return saved ? JSON.parse(saved) : [];
        } catch (error) {
            console.error('[MessageQueue] Failed to load queue from localStorage', error);
            return [];
        }
    }

    /**
     * Save queue to localStorage with microtask batching
     * Multiple calls within the same event loop tick are coalesced into a single write
     */
    saveQueue() {
        // If already scheduled, the pending write will capture the latest state
        if (this._saveScheduled) return;

        this._saveScheduled = true;

        queueMicrotask(() => {
            this._saveScheduled = false;
            try {
                localStorage.setItem('chat_message_queue', JSON.stringify(this.queue));
            } catch (error) {
                console.error('[MessageQueue] Failed to save queue to localStorage', error);
            }
        });
    }

    /**
     * Force save immediately (bypasses batching)
     * Use sparingly - only when persistence is critical before navigation
     */
    saveQueueSync() {
        this._saveScheduled = false; // Cancel any pending microtask
        try {
            localStorage.setItem('chat_message_queue', JSON.stringify(this.queue));
        } catch (error) {
            console.error('[MessageQueue] Failed to sync save queue to localStorage', error);
        }
    }

    /**
     * Add message to queue
     * @param {Object} message - Message data to queue
     * @returns {string} Message ID
     */
    add(message) {
        // FIX #5: Cap queue size to prevent localStorage quota exhaustion
        if (this.queue.length >= MAX_QUEUE_SIZE) {
            console.warn('[MessageQueue] Queue is full, cleaning up old failed messages first');
            this.cleanupOldMessages();
            if (this.queue.length >= MAX_QUEUE_SIZE) {
                console.error('[MessageQueue] Queue still full after cleanup, rejecting message');
                return null;
            }
        }
        const queuedMessage = {
            ...message,
            id: `queued_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
            status: 'pending',
            queuedAt: Date.now(),
            retryCount: 0,
        };
        this.queue.push(queuedMessage);
        this.saveQueue(); // Batched via microtask
        this.notify();
        return queuedMessage.id;
    }

    /**
     * Remove message from queue by ID
     * @param {string} id - Message ID to remove
     */
    remove(id) {
        this.queue = this.queue.filter(m => m.id !== id);
        this.saveQueue(); // Batched via microtask
        this.notify();
    }

    /**
     * Update message status
     * @param {string} id - Message ID
     * @param {string} status - New status ('pending' | 'sending' | 'failed' | 'sent')
     */
    updateStatus(id, status) {
        const message = this.queue.find(m => m.id === id);
        if (message) {
            message.status = status;
            if (status === 'failed') {
                message.retryCount = (message.retryCount || 0) + 1;
            }
            this.saveQueue(); // Batched via microtask
            this.notify();
        }
    }

    /**
     * Update message data generic
     * @param {string} id - Message ID
     * @param {Object} updates - Data to update (shallow merge)
     */
    update(id, updates) {
        const message = this.queue.find(m => m.id === id);
        if (message) {
            Object.assign(message, updates);
            this.saveQueue(); // Batched via microtask
            this.notify();
        }
    }

    /**
     * Get all queued messages
     * @returns {Array} Array of queued messages
     */
    getAll() {
        return this.queue;
    }

    /**
     * Get queued messages for a specific conversation
     * @param {string} conversationId - Conversation ID
     * @returns {Array} Array of queued messages for this conversation
     */
    getByConversation(conversationId) {
        return this.queue.filter(m => m.conversationId === conversationId);
    }

    /**
     * Clear all messages from queue
     */
    clear() {
        this.queue = [];
        this.saveQueue(); // Batched via microtask
        this.notify();
    }

    /**
     * Remove old failed messages (>24 hours)
     */
    cleanupOldMessages() {
        const oneDayAgo = Date.now() - (24 * 60 * 60 * 1000);
        const originalLength = this.queue.length;
        this.queue = this.queue.filter(m => {
            return m.status !== 'failed' || m.queuedAt > oneDayAgo;
        });
        if (this.queue.length !== originalLength) {
            this.saveQueue(); // Batched via microtask
            this.notify();
        }
    }

    /**
     * Subscribe to queue changes
     * @param {Function} callback - Callback function to call on changes
     * @returns {Function} Unsubscribe function
     */
    subscribe(callback) {
        this.listeners.push(callback);
        return () => {
            this.listeners = this.listeners.filter(l => l !== callback);
        };
    }

    /**
     * Notify all subscribers of queue changes
     */
    notify() {
        this.listeners.forEach(listener => {
            try {
                listener(this.queue);
            } catch (error) {
                console.error('[MessageQueue] Listener error', error);
            }
        });
    }
}

// Export singleton instance
export const messageQueue = new MessageQueue();

// Cleanup old messages on load
messageQueue.cleanupOldMessages();
